import React, { useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { 
  Box, 
  Button, 
  Heading, 
  Text, 
  Spinner, 
  Stack, 
  useToast,
  VStack,
  HStack,
  Icon,
  Flex,
  Link
} from "@chakra-ui/react";
import { useUser } from "./userRole/UserContext";
import { useCurrency } from "./CurrencyExchange/CurrencyContext";
import { useTranslation } from "./LanguageTranslation/TranslationContext";
import { login } from "../auth";
import axios from "axios";
import { FaEnvelope } from "react-icons/fa";

const EmailVerification = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const toast = useToast();
  const history = useHistory();
  const { setUser } = useUser();
  const { setLanguage } = useTranslation();
  const { setCurrency } = useCurrency();

  const handleVerifyEmail = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`/auth/verify_email/${token}`);
      const data = response.data;
      
      if (response.status === 200 && data.access_token) {
        // Log in the user
        login(data);
        localStorage.setItem("email", data.email);
        localStorage.setItem("username", data.username);
        setLanguage(data.settings?.preferred_language || "en");
        setCurrency(data.settings?.preferred_currency || "USD");
        setUser({
          role: data.role,
          organization: data.organization,
        });
        setSuccess(true);
        toast({
          title: "Email Verified!",
          description: "Your email has been verified successfully. Welcome to NOMAD360!",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        setError(data.message || "Verification failed.");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Network error. Please try again later.";
      setError(errorMessage);
      toast({
        title: "Verification Failed",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    history.push("/ai-travel-planner"); // or your main app route
  };

  const handleResendCode = () => {
    // Implement resend code functionality
    toast({
      title: "Code Resent",
      description: "A new verification code has been sent to your email.",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
  };

  return (
    <Box 
      minH="100vh" 
      display="flex" 
      alignItems="center" 
      justifyContent="center"
      bg="gray.50"
      p={4}
    >
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="lg"
        p={8}
        maxW="md"
        w="full"
        textAlign="center"
      >
        {loading ? (
          <VStack spacing={6}>
            <Spinner size="xl" color="customGreen.500" />
            <Text fontSize="lg" color="gray.600">
              Verifying your email...
            </Text>
          </VStack>
        ) : success ? (
          <VStack spacing={6}>
            <Box
              w="80px"
              h="80px"
              borderRadius="full"
              bg="customGreen.100"
              display="flex"
              alignItems="center"
              justifyContent="center"
              mx="auto"
            >
              <Icon as={FaEnvelope} w="40px" h="40px" color="customGreen.500" />
            </Box>
            <Heading as="h2" size="lg" color="customGreen.500">
              Email Verified!
            </Heading>
            <Text fontSize="md" color="gray.600" textAlign="center">
              Your email has been verified successfully. Welcome to NOMAD360!
            </Text>
            <Button 
              colorScheme="customGreen" 
              borderRadius="full" 
              onClick={handleContinue}
              size="lg"
              w="full"
            >
              Continue
            </Button>
          </VStack>
        ) : (
          <VStack spacing={6}>
            <Box
              w="80px"
              h="80px"
              borderRadius="full"
              bg="customGreen.100"
              display="flex"
              alignItems="center"
              justifyContent="center"
              mx="auto"
            >
              <Icon as={FaEnvelope} w="40px" h="40px" color="customGreen.500" />
            </Box>
            <Heading as="h2" size="lg" color="customGreen.500" mb={0}>
              Verify your email
            </Heading>
            <Text fontSize="md" color="gray.600" textAlign="center" lineHeight="tall" mb={0}>
              We have sent a verification code to your email address.
              Please check your inbox and click the button below to
              activate your account.
            </Text>
            {error && (
              <Text fontSize="sm" color="red.500" textAlign="center" mb={0}>
                {error}
              </Text>
            )}
            <Button 
              colorScheme="customGreen" 
              borderRadius="full" 
              onClick={handleVerifyEmail}
              isLoading={loading}
              loadingText="Verifying..."
              size="lg"
              w="full"
              bgGradient="linear(to-b, customGreen.400, customGreen.500)"
              _hover={{
                bgGradient: "linear(to-b, customGreen.500, customGreen.600)"
              }}
            >
              Verify email
            </Button>
            {/* <Flex justify="center" align="center" fontSize="sm">
              <Text color="gray.500" mb={0}>Didn't receive the code? </Text>
              <Link 
                color="blue.500" 
                ml={1}
                onClick={handleResendCode}
                _hover={{ textDecoration: "underline" }}
              >
                Resend Code
              </Link>
            </Flex> */}
          </VStack>
        )}
      </Box>
    </Box>
  );
};

export default EmailVerification; 